import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import { ChevronDown, ChevronRight, Eye, GripVertical, Library, Plus, Trash2 } from 'lucide-react';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import Modal from '../../../components/modal';
import DialogModal from '../../../components/dialog';
import {
	listFaq,
	listFaqBank,
	createFaqCategory,
	removeFaqCategory,
	createFaqItem,
	updateFaqItem,
	removeFaqItem,
	reorderFaqItems,
	addFaqItemFromBank,
	publishFaq,
} from '../../../utils/queries';

const emptyItemForm = { question: '', answer: '' };
const emptyCategoryForm = { name: '', sortOrder: 0 };

function FaqItemRow({ item, onEdit, onRemove }) {
	const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.itemId });
	const style = {
		transform: CSS.Transform.toString(transform),
		transition,
		opacity: isDragging ? 0.5 : 1,
	};

	return (
		<div
			ref={setNodeRef}
			style={style}
			className='flex items-start gap-3 border border-gray-200 rounded-md px-3 py-2 bg-white'
		>
			<button type='button' className='mt-0.5 text-gray-400 hover:text-gray-600 cursor-grab' {...attributes} {...listeners}>
				<GripVertical className='size-4' />
			</button>
			<button type='button' className='min-w-0 flex-1 text-left' onClick={() => onEdit()}>
				<p className='text-sm text-black font-medium hover:underline truncate'>{item.question}</p>
			</button>
			<button type='button' className='mt-0.5 text-gray-400 hover:text-gray-600' onClick={() => onRemove()}>
				<Trash2 className='size-4' />
			</button>
		</div>
	);
}

function AddedQuestionsList({ items, onEditItem, onRemoveItem, onReorderItems }) {
	const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

	const handleDragEnd = (event) => {
		const { active, over } = event;
		if (!over || active.id === over.id) return;
		const oldIndex = items.findIndex((i) => i.itemId === active.id);
		const newIndex = items.findIndex((i) => i.itemId === over.id);
		onReorderItems(arrayMove(items, oldIndex, newIndex));
	};

	if (items.length === 0) {
		return <p className='text-sm text-gray-400'>No questions added yet.</p>;
	}

	return (
		<DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
			<SortableContext items={items.map((i) => i.itemId)} strategy={verticalListSortingStrategy}>
				<div className='space-y-2'>
					{items.map((item) => (
						<FaqItemRow key={item.itemId} item={item} onEdit={() => onEditItem(item)} onRemove={() => onRemoveItem(item)} />
					))}
				</div>
			</SortableContext>
		</DndContext>
	);
}

function BankCategoryBrowser({ bankCategories, storeCategories, openSlugs, onToggleOpen, onAddSelected, onAddAll }) {
	const findStoreCategoryForBank = (bankCategory) =>
		storeCategories.find((c) => c.sourceBankCategoryId === bankCategory.categoryId);

	return (
		<div className='space-y-3'>
			{bankCategories.map((bankCategory) => {
				const storeCategory = findStoreCategoryForBank(bankCategory);
				const open = openSlugs.has(bankCategory.slug);
				const addedBankItemIds = new Set(
					(storeCategory?.items ?? []).map((i) => i.sourceBankItemId).filter(Boolean),
				);
				const available = bankCategory.items.filter((item) => !addedBankItemIds.has(item.itemId));

				return (
					<BankCategoryRow
						key={bankCategory.categoryId}
						bankCategory={bankCategory}
						storeCategory={storeCategory}
						available={available}
						open={open}
						onToggleOpen={() => onToggleOpen(bankCategory.slug)}
						onAddSelected={(ids) => onAddSelected(bankCategory, ids)}
						onAddAll={() => onAddAll(bankCategory, available.map((i) => i.itemId))}
					/>
				);
			})}
		</div>
	);
}

function BankCategoryRow({ bankCategory, storeCategory, available, open, onToggleOpen, onAddSelected, onAddAll }) {
	const [selected, setSelected] = useState(new Set());

	const toggle = (itemId) => {
		setSelected((prev) => {
			const next = new Set(prev);
			if (next.has(itemId)) {
				next.delete(itemId);
			} else {
				next.add(itemId);
			}
			return next;
		});
	};

	return (
		<div className='border border-gray-200 rounded-md'>
			<div className='flex items-center gap-3 px-4 py-3 cursor-pointer' onClick={onToggleOpen}>
				<span className='flex-1 font-medium text-black text-base'>{bankCategory.name}</span>
				{storeCategory && storeCategory.items.length > 0 && (
					<span className='text-xs text-gray-400'>{storeCategory.items.length} already added</span>
				)}
				<button type='button' className='text-gray-400 hover:text-gray-600'>
					{open ? <ChevronDown className='size-4' /> : <ChevronRight className='size-4' />}
				</button>
			</div>

			{open && (
				<div className='px-4 pb-4 space-y-3 border-t border-gray-100 pt-3'>
					{available.length === 0 ? (
						<p className='text-sm text-gray-400'>All questions from this category have been added.</p>
					) : (
						<>
							<div className='space-y-1'>
								{available.map((item) => (
									<label key={item.itemId} className='flex items-start gap-2 text-sm px-1 py-1 cursor-pointer'>
										<input
											type='checkbox'
											className='mt-1 accent-black'
											checked={selected.has(item.itemId)}
											onChange={() => toggle(item.itemId)}
										/>
										<span>{item.question}</span>
									</label>
								))}
							</div>
							<div className='flex gap-2'>
								<button
									type='button'
									className='btn-secondary-small'
									disabled={selected.size === 0}
									onClick={() => {
										onAddSelected([...selected]);
										setSelected(new Set());
									}}
								>
									Add Selected{selected.size > 0 ? ` (${selected.size})` : ''}
								</button>
								<button type='button' className='btn-secondary-small' onClick={onAddAll}>
									Add All ({available.length})
								</button>
							</div>
						</>
					)}
				</div>
			)}
		</div>
	);
}

function FaqBankModal({ open, setOpen, bankCategories, storeCategories, onAddSelected, onAddAll }) {
	const [openSlugs, setOpenSlugs] = useState(new Set());

	const toggleOpen = (slug) => {
		setOpenSlugs((prev) => {
			const next = new Set(prev);
			if (next.has(slug)) {
				next.delete(slug);
			} else {
				next.add(slug);
			}
			return next;
		});
	};

	return (
		<Modal open={open} setOpen={setOpen} title='Question Bank' size='lg'>
			<div className='space-y-4'>
				<p className='text-sm text-gray-500'>
					Browse common questions by category and pick the ones you want to add to your store's FAQ.
				</p>
				<div className='max-h-[60vh] overflow-y-auto pr-1'>
					<BankCategoryBrowser
						bankCategories={bankCategories}
						storeCategories={storeCategories}
						openSlugs={openSlugs}
						onToggleOpen={toggleOpen}
						onAddSelected={onAddSelected}
						onAddAll={onAddAll}
					/>
				</div>
				<div className='flex justify-end pt-2'>
					<button type='button' className='btn-secondary' onClick={() => setOpen(false)}>
						Done
					</button>
				</div>
			</div>
		</Modal>
	);
}

function FaqPreviewModal({ open, setOpen, storeCategories, published }) {
	const nonEmptyCategories = storeCategories.filter((c) => c.items.length > 0);

	return (
		<Modal open={open} setOpen={setOpen} title='FAQ Preview' size='lg'>
			<div className='space-y-6'>
				<p className='text-sm text-gray-500'>
					This shows how your FAQ will look once published.{' '}
					{!published && (
						<span className='font-semibold text-amber-600'>
							It isn't live yet -- click Publish to make it visible on your storefront.
						</span>
					)}
				</p>

				{nonEmptyCategories.length === 0 && <p className='text-sm text-gray-400'>Nothing to preview yet.</p>}

				<div className='max-h-[70vh] overflow-y-auto space-y-8 pr-1'>
					{nonEmptyCategories.map((category) => (
						<div key={category.categoryId}>
							<h3 className='text-lg font-semibold text-black mb-3'>{category.name}</h3>
							<div className='space-y-4'>
								{category.items.map((item) => (
									<div key={item.itemId}>
										<p className='font-medium text-black'>{item.question}</p>
										<p className='text-sm text-gray-600 mt-1 whitespace-pre-line'>{item.answer}</p>
									</div>
								))}
							</div>
						</div>
					))}
				</div>
			</div>
		</Modal>
	);
}

export default function FaqTab({ storeId, headerActions }) {
	const [storeCategories, setStoreCategories] = useState([]);
	const [published, setPublished] = useState(false);
	const [bankCategories, setBankCategories] = useState([]);
	const [loading, setLoading] = useState(true);
	const [bankLoading, setBankLoading] = useState(true);
	const [openCategoryIds, setOpenCategoryIds] = useState(new Set());
	const [publishing, setPublishing] = useState(false);
	const [previewOpen, setPreviewOpen] = useState(false);
	const [bankModalOpen, setBankModalOpen] = useState(false);

	const [itemModalOpen, setItemModalOpen] = useState(false);
	const [editingItem, setEditingItem] = useState(null);
	const [activeCategory, setActiveCategory] = useState(null);
	const [itemForm, setItemForm] = useState(emptyItemForm);
	const [itemSaving, setItemSaving] = useState(false);
	const [itemError, setItemError] = useState('');

	const [categoryModalOpen, setCategoryModalOpen] = useState(false);
	const [categoryForm, setCategoryForm] = useState(emptyCategoryForm);
	const [categorySaving, setCategorySaving] = useState(false);
	const [categoryError, setCategoryError] = useState('');

	const [confirmState, setConfirmState] = useState(null);
	const closeConfirm = () => setConfirmState(null);
	const handleConfirm = async () => {
		const action = confirmState?.onConfirm;
		closeConfirm();
		if (action) await action();
	};

	const refreshStore = () => {
		if (!storeId) {
			setLoading(false);
			return;
		}
		setLoading(true);
		listFaq(storeId).then((result) => {
			if (result?.success) {
				setStoreCategories(result.data.categories);
				setPublished(!!result.data.published);
			}
			setLoading(false);
		});
	};

	const refreshBank = () => {
		setBankLoading(true);
		listFaqBank().then((result) => {
			if (result?.success) setBankCategories(result.data.categories);
			setBankLoading(false);
		});
	};

	useEffect(() => {
		refreshStore();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [storeId]);

	useEffect(() => {
		refreshBank();
	}, []);

	// The FAQ is published/unpublished as a whole (Store.faqPublishedFlag on the
	// backend), not per question -- any edit flips this back to false server-side
	// until the admin explicitly re-publishes.
	const hasAnyQuestions = useMemo(() => storeCategories.some((c) => c.items.length > 0), [storeCategories]);
	const hasUnpublishedChanges = !published && hasAnyQuestions;

	// Warn on tab close/refresh -- in-app route changes can't be blocked without
	// migrating this app off <BrowserRouter>/<Routes> to a data router, so the
	// banner below is the reminder for in-app navigation.
	useEffect(() => {
		const handler = (e) => {
			if (!hasUnpublishedChanges) return;
			e.preventDefault();
			e.returnValue = '';
		};
		window.addEventListener('beforeunload', handler);
		return () => window.removeEventListener('beforeunload', handler);
	}, [hasUnpublishedChanges]);

	const toggleCategoryOpen = (categoryId) => {
		setOpenCategoryIds((prev) => {
			const next = new Set(prev);
			if (next.has(categoryId)) {
				next.delete(categoryId);
			} else {
				next.add(categoryId);
			}
			return next;
		});
	};

	// ── Publish ──

	const handlePublish = async () => {
		if (published) return;
		setPublishing(true);
		const result = await publishFaq(storeId);
		setPublishing(false);
		if (!result?.success) {
			toast.error(result?.error?.message || 'Failed to publish FAQ.');
			return;
		}
		toast.success('Your FAQ is now live on your store.');
		refreshStore();
	};

	// ── Question bank ──

	const handleAddSelectedFromBank = async (bankCategory, bankItemIds) => {
		const results = await Promise.all(bankItemIds.map((bankItemId) => addFaqItemFromBank(bankItemId, storeId)));
		const failed = results.filter((r) => !r?.success);
		if (failed.length > 0) {
			toast.error(`Failed to add ${failed.length} question${failed.length === 1 ? '' : 's'}.`);
		}
		refreshStore();
	};

	// ── Questions ──

	const openEditItem = (category, item) => {
		setEditingItem(item);
		setActiveCategory(category);
		setItemForm({ question: item.question, answer: item.answer });
		setItemError('');
		setItemModalOpen(true);
	};

	const openCustomQuestion = (category) => {
		setEditingItem(null);
		setActiveCategory(category);
		setItemForm(emptyItemForm);
		setItemError('');
		setItemModalOpen(true);
	};

	const handleItemSubmit = async (e) => {
		e.preventDefault();
		setItemSaving(true);
		setItemError('');

		const result = editingItem
			? await updateFaqItem(editingItem.itemId, itemForm)
			: await createFaqItem({
					...itemForm,
					storeId,
					categoryId: activeCategory.categoryId,
					sortOrder: activeCategory.items.length,
				});

		setItemSaving(false);
		if (!result?.success) {
			setItemError(result?.error?.message || 'Failed to save.');
			return;
		}
		setItemModalOpen(false);
		refreshStore();
	};

	const handleRemoveItem = (item) => {
		setConfirmState({
			title: 'Delete question?',
			message: `Delete "${item.question}"?`,
			confirmLabel: 'Delete',
			onConfirm: async () => {
				const result = await removeFaqItem(item.itemId);
				if (!result?.success) {
					toast.error(result?.error?.message || 'Failed to delete question.');
					return;
				}
				refreshStore();
			},
		});
	};

	const handleReorderItems = (categoryId, reorderedItems) => {
		setStoreCategories((prev) => prev.map((c) => (c.categoryId === categoryId ? { ...c, items: reorderedItems } : c)));
		reorderFaqItems(
			categoryId,
			reorderedItems.map((i) => i.itemId),
		);
	};

	// ── Categories ──

	const openCategoryModal = () => {
		setCategoryForm(emptyCategoryForm);
		setCategoryError('');
		setCategoryModalOpen(true);
	};

	const handleCategorySubmit = async (e) => {
		e.preventDefault();
		setCategorySaving(true);
		setCategoryError('');
		const result = await createFaqCategory({ ...categoryForm, storeId });
		setCategorySaving(false);
		if (!result?.success) {
			setCategoryError(result?.error?.message || 'Failed to create category.');
			return;
		}
		setCategoryModalOpen(false);
		refreshStore();
	};

	const handleRemoveCategory = (category) => {
		const count = category.items.length;
		setConfirmState({
			title: 'Delete category?',
			message:
				count > 0
					? `Delete "${category.name}" and its ${count} question${count === 1 ? '' : 's'}? This cannot be undone.`
					: `Delete "${category.name}"?`,
			confirmLabel: 'Delete',
			onConfirm: async () => {
				const result = await removeFaqCategory(category.categoryId);
				if (!result?.success) {
					toast.error(result?.error?.message || 'Failed to delete category.');
					return;
				}
				refreshStore();
			},
		});
	};

	if (loading || bankLoading) {
		return <p className='text-sm text-gray-400'>Loading...</p>;
	}

	const headerButtons = (
		<>
			<button
				type='button'
				className='btn-secondary flex items-center gap-1.5'
				disabled={!storeId}
				onClick={() => setBankModalOpen(true)}
			>
				<Library className='size-4' />
				Question Bank
			</button>
			<button type='button' className='btn-secondary flex items-center gap-1.5' onClick={() => setPreviewOpen(true)}>
				<Eye className='size-4' />
				Preview
			</button>
			<button
				type='button'
				className='btn-primary'
				disabled={!hasUnpublishedChanges || publishing}
				onClick={handlePublish}
			>
				{publishing ? 'Publishing...' : 'Publish'}
			</button>
		</>
	);

	return (
		<div className='max-w-3xl space-y-8'>
			{headerActions && createPortal(headerButtons, headerActions)}

			{hasUnpublishedChanges && (
				<div className="rounded-md border px-4 py-3 border-amber-300 bg-amber-50 flex items-center gap-3">
					<span className='inline-flex items-center rounded-full bg-amber-200 px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-amber-800 shrink-0'>
						Draft
					</span>
					<p className='text-sm font-medium text-amber-700'>
						You have unpublished changes. Click Publish to make your FAQ live on your store.
					</p>
				</div>
			)}

			<p className='text-sm text-gray-500 mb-4'>
				Create your store's FAQ categories and questions, or pull sample questions in from the question bank.
				Changes won't appear on your storefront until you click Publish.
			</p>

			{storeCategories.length === 0 && (
				<p className='text-sm text-gray-400'>
					No categories yet. Create one below, or add questions from the Question Bank to get started.
				</p>
			)}

			<div className='space-y-3'>
				{storeCategories.map((category) => {
					const open = openCategoryIds.has(category.categoryId);
					return (
						<div key={category.categoryId} className='border border-gray-200 rounded-md'>
							<div
								className='flex items-center gap-3 px-4 py-3 cursor-pointer'
								onClick={() => toggleCategoryOpen(category.categoryId)}
							>
								<span className='flex-1 font-medium text-black text-base'>{category.name}</span>
								<span className='text-xs text-gray-400'>
									{category.items.length} question{category.items.length === 1 ? '' : 's'}
								</span>
								<button
									type='button'
									className='text-gray-400 hover:text-gray-600'
									onClick={(e) => {
										e.stopPropagation();
										handleRemoveCategory(category);
									}}
								>
									<Trash2 className='size-4' />
								</button>
								<button
									type='button'
									className='text-gray-400 hover:text-gray-600'
									onClick={(e) => {
										e.stopPropagation();
										toggleCategoryOpen(category.categoryId);
									}}
								>
									{open ? <ChevronDown className='size-4' /> : <ChevronRight className='size-4' />}
								</button>
							</div>
							{open && (
								<div className='px-4 pb-4 space-y-3 border-t border-gray-100 pt-3'>
									<div className='flex justify-end'>
										<button
											type='button'
											className='btn-secondary-small'
											onClick={() => openCustomQuestion(category)}
										>
											Add Question
										</button>
									</div>
									<AddedQuestionsList
										items={category.items}
										onEditItem={(item) => openEditItem(category, item)}
										onRemoveItem={handleRemoveItem}
										onReorderItems={(reordered) => handleReorderItems(category.categoryId, reordered)}
									/>
								</div>
							)}
						</div>
					);
				})}
			</div>

			<div>
				<button
					type='button'
					className='btn-secondary-small rounded-full p-1.5'
					aria-label='New Category'
					disabled={!storeId}
					onClick={openCategoryModal}
				>
					<Plus className='size-4' />
				</button>
			</div>

			<Modal
				open={itemModalOpen}
				setOpen={setItemModalOpen}
				title={editingItem ? 'Edit Question' : 'New Question'}
				size='lg'
			>
				<form onSubmit={handleItemSubmit} className='space-y-4'>
					{itemError && <div className='text-sm text-pi-red'>{itemError}</div>}
					<div>
						<label>Question</label>
						<textarea
							className='form-input w-full'
							rows={2}
							required
							value={itemForm.question}
							onChange={(e) => setItemForm({ ...itemForm, question: e.target.value })}
						/>
					</div>
					<div>
						<label>Answer</label>
						<textarea
							className='xform-input w-full border p-3'
							rows={4}
							required
							value={itemForm.answer}
							onChange={(e) => setItemForm({ ...itemForm, answer: e.target.value })}
						/>
					</div>
					<div className='flex gap-3 pt-2'>
						<button type='submit' disabled={itemSaving} className='btn-primary'>
							{itemSaving ? 'Saving...' : 'Save'}
						</button>
						<button type='button' className='btn-secondary' onClick={() => setItemModalOpen(false)}>
							Cancel
						</button>
					</div>
				</form>
			</Modal>

			<Modal open={categoryModalOpen} setOpen={setCategoryModalOpen} title='New Category'>
				<form onSubmit={handleCategorySubmit} className='space-y-4'>
					{categoryError && <div className='text-sm text-pi-red'>{categoryError}</div>}
					<div>
						<label>Name</label>
						<input
							className='form-input'
							required
							value={categoryForm.name}
							onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
						/>
					</div>
					<div className='w-1/5'>
						<label>Sort Order</label>
						<input
							type='number'
							className='form-input'
							value={categoryForm.sortOrder}
							onChange={(e) => setCategoryForm({ ...categoryForm, sortOrder: Number(e.target.value) })}
						/>
					</div>
					<div className='flex gap-3 pt-2'>
						<button type='submit' disabled={categorySaving} className='btn-primary'>
							{categorySaving ? 'Saving...' : 'Save'}
						</button>
						<button type='button' className='btn-secondary' onClick={() => setCategoryModalOpen(false)}>
							Cancel
						</button>
					</div>
				</form>
			</Modal>

			<FaqBankModal
				open={bankModalOpen}
				setOpen={setBankModalOpen}
				bankCategories={bankCategories}
				storeCategories={storeCategories}
				onAddSelected={handleAddSelectedFromBank}
				onAddAll={handleAddSelectedFromBank}
			/>

			<FaqPreviewModal
				open={previewOpen}
				setOpen={setPreviewOpen}
				storeCategories={storeCategories}
				published={published}
			/>

			<DialogModal open={!!confirmState} setOpen={(open) => !open && closeConfirm()} title={confirmState?.title}>
				<p className='text-sm text-gray-600'>{confirmState?.message}</p>
				<div className='flex justify-end gap-2 pt-2'>
					<button type='button' className='btn-secondary' onClick={closeConfirm}>
						Cancel
					</button>
					<button type='button' className='btn-alert' onClick={handleConfirm}>
						{confirmState?.confirmLabel || 'Confirm'}
					</button>
				</div>
			</DialogModal>
		</div>
	);
}
